/* eslint-disable camelcase */

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { autorun } from 'mobx';
import { observer } from 'mobx-react';
import { isEmpty, minBy, maxBy } from 'lodash';
import { extractFromSlug } from 'helpers/utils';
import useMobxStores from 'hooks/useMobxStores';

const canUseDOM = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
);

const IntercomAPI = (...args) => {
  if (canUseDOM && window.Intercom) {
    window.Intercom.apply(null, args);
  }
};

const Intercom = (props) => {
  const { appID } = props;
  const { userStore, eventStore, uiStore } = useMobxStores();
  const { isIntercomHidden } = uiStore;

  const getUserParams = () => {
    const { subscription, rhlsCourseEnrollments, user, enablementsQuotas } =
      userStore;

    // example: "rh124ls,rh125ls,..."
    let rhlsCourseListStr = rhlsCourseEnrollments
      .map((enrollment) => {
        const { code } = extractFromSlug(enrollment.slug_tag);
        return `${code}ls`; // e.g. "rh124ls"
      })
      .join(',');

    // max 255 characters. This will probably never happen, but just in case.
    if (rhlsCourseListStr.length > 255) {
      rhlsCourseListStr = `multiple_rhls_courses=${rhlsCourseEnrollments.length}`;
    }

    // if there is a subscription, add the subscription properties
    let subscriptionProps = subscription
      ? {
          ...(subscription.subscription && {
            subscription: subscription.subscription,
          }),
          ...(subscription.start_time && {
            subscription_start_time: subscription.start_time,
          }),
          ...(subscription.end_time && {
            subscription_end_time: subscription.end_time,
          }),
        }
      : {};

    // if there is no subscription and there are rhls course enrollments, set the subscription to rhls_course
    if (isEmpty(subscriptionProps) && rhlsCourseEnrollments?.length > 0) {
      const earliestStart = minBy(
        rhlsCourseEnrollments,
        'start_time',
      )?.start_time;
      const latestEnd = maxBy(rhlsCourseEnrollments, 'end_time')?.end_time;

      subscriptionProps = {
        subscription: 'rhls_course',
        ...(earliestStart && { subscription_start_time: earliestStart }),
        ...(latestEnd && { subscription_end_time: latestEnd }),
      };
    }

    return {
      ...(user.uuid && { user_id: user.uuid }),
      ...(user.preferred_name && {
        name: user.preferred_name,
      }),
      ...(user.default_email && {
        email: user.default_email,
      }),
      ...subscriptionProps,
      ...(!!enablementsQuotas?.pdf_downloads?.allowed && {
        subscription_enablements_pdf_downloads_allowed:
          enablementsQuotas?.pdf_downloads?.allowed,
      }),
      ...(rhlsCourseListStr && {
        rhls_course_list: rhlsCourseListStr,
      }),
      ...user,
    };
  };

  useEffect(() => {
    if (!appID || !canUseDOM) {
      return false;
    }

    if (!window.Intercom) {
      ((w, d, id, s) => {
        function i(...args) {
          i.c(args);
        }
        i.q = [];
        i.c = (args) => {
          i.q.push(args);
        };
        w.Intercom = i;
        s = d.createElement('script');
        s.async = 1;
        s.src = `https://widget.intercom.io/widget/${id}`;
        d.head.appendChild(s);
      })(window, document, appID);
    }

    if (window.Intercom) {
      window.Intercom('boot', {
        app_id: appID,
      });
    }

    const dispose = autorun(() => {
      const {
        user: { uuid },
        isLoggedIn,
        subscription,
        rhlsCourseEnrollments,
      } = userStore;
      const subscriptionCode = subscription?.subscription;

      if (!window.Intercom) return;

      if (!isLoggedIn) {
        if (window.Intercom.booted) window.Intercom('shutdown');
        window.Intercom('boot', { app_id: appID });
        return;
      }

      if (uuid && (subscriptionCode || rhlsCourseEnrollments.length > 0)) {
        window.Intercom('update', getUserParams());
      }
    });

    return () => {
      dispose();
      if (window.Intercom) {
        window.Intercom('shutdown');
        delete window.Intercom;
        delete window.intercomSettings;
      }
    };
  }, []);

  useEffect(() => {
    // Hide Intercom when the Drawer is open or when any component has registered a hide reason (e.g. AI assistant sidebar).
    // When Intercom chat is open, the class changes from intercom-launcher to intercom-app, so we check both.
    const elementIntercomLauncher =
      document.getElementsByClassName('intercom-launcher')[0] ||
      document.getElementsByClassName('intercom-app')[0];
    if (elementIntercomLauncher) {
      const shouldHide = eventStore.isDrawerOpen || isIntercomHidden;
      elementIntercomLauncher.style.display = shouldHide ? 'none' : 'block';
    }
  }, [eventStore.isDrawerOpen, isIntercomHidden]);

  return null;
};

Intercom.propTypes = {
  appID: PropTypes.string.isRequired,
};

Intercom.displayName = 'Intercom';

export { IntercomAPI };

export default observer(Intercom);
